<script setup lang="ts">
import type { ConditionView, DefaultModelMode } from '~/features/my-wallet/types/receiving-model'
import { I18nT, useI18n } from 'vue-i18n'
import BlockError from '~/features/my-wallet/components/BlockError.vue'
import MilestoneStepper from '~/features/my-wallet/components/charts/MilestoneStepper.vue'
import ConditionCheck from '~/features/my-wallet/components/ConditionCheck.vue'
import { PERFORMANCE_WINDOW_DAYS } from '~/features/my-wallet/constants/my-wallet'

interface Props {
  conditions: ConditionView[] | null
  mode: DefaultModelMode
  milestone: { completed: number, total: number } | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  retry: []
}>()

const { t } = useI18n()

const CONDITIONS_KEY_PATH = 'my_wallet.receiving_model.card.conditions'

// The default and essential cards are a checklist: the conditions are fixed text, each one with the done marker.
const isChecklist = computed(() => props.mode === 'default' || props.mode === 'essential')

interface KnownCondition {
  needsValue: boolean
  keyPathByMode: Record<DefaultModelMode, string | null>
}

const KNOWN_CONDITIONS: Record<string, KnownCondition> = {
  REGISTRATION_ENABLED: {
    needsValue: false,
    keyPathByMode: {
      default: `${CONDITIONS_KEY_PATH}.REGISTRATION_ENABLED.requirement`,
      novice: `${CONDITIONS_KEY_PATH}.REGISTRATION_ENABLED.for_whom`,
      essential: `${CONDITIONS_KEY_PATH}.REGISTRATION_ENABLED.for_whom`,
    },
  },
  FIRST_INSTALLATIONS_MANDATORY: {
    needsValue: true,
    keyPathByMode: { default: null, novice: `${CONDITIONS_KEY_PATH}.FIRST_INSTALLATIONS_MANDATORY`, essential: null },
  },
  ONLY_MODEL_BELOW_THRESHOLD: {
    needsValue: true,
    keyPathByMode: { default: `${CONDITIONS_KEY_PATH}.ONLY_MODEL_BELOW_THRESHOLD`, novice: null, essential: null },
  },
}

const MILESTONE_ANCHOR_CODE = 'FIRST_INSTALLATIONS_MANDATORY'

type ConditionListItem
  = | { kind: 'error' }
    | { kind: 'condition', code: string, keyPath: string, value: number | null }
    | { kind: 'fallback' }
    | { kind: 'milestone', completed: number, total: number }

function toConditionItem(condition: ConditionView, mode: DefaultModelMode): ConditionListItem | null {
  const knownCondition = KNOWN_CONDITIONS[condition.code]
  if (!knownCondition)
    return { kind: 'fallback' }
  const keyPath = knownCondition.keyPathByMode[mode]
  if (!keyPath)
    return null
  if (knownCondition.needsValue && condition.value === null)
    return { kind: 'fallback' }
  return { kind: 'condition', code: condition.code, keyPath, value: condition.value }
}

const conditionItems = computed<ConditionListItem[]>(() => {
  const milestoneItems: ConditionListItem[] = props.milestone ? [{ kind: 'milestone', ...props.milestone }] : []
  if (!props.conditions)
    return [{ kind: 'error' }, ...milestoneItems]

  return props.conditions.flatMap((condition) => {
    const conditionItem = toConditionItem(condition, props.mode)
    if (!conditionItem)
      return []
    const isMilestoneAnchor = conditionItem.kind === 'condition' && conditionItem.code === MILESTONE_ANCHOR_CODE
    return isMilestoneAnchor ? [conditionItem, ...milestoneItems] : [conditionItem]
  })
})
</script>

<template>
  <ul class="default-model-conditions" :class="{ '-checklist': isChecklist }">
    <template v-for="(item, index) in conditionItems" :key="`${item.kind}-${index}`">
      <li v-if="item.kind === 'error'" class="default-model-error">
        <BlockError id="my-wallet-model-c-conditions-error" @retry="emit('retry')" />
      </li>
      <li v-else-if="item.kind === 'milestone'" class="default-model-milestone">
        <MilestoneStepper
          size="small"
          :completed="item.completed"
          :total="item.total"
          :label="t('my_wallet.receiving_model.novice.stepper_aria', { completed: item.completed, total: item.total })"
        />
        <I18nT keypath="my_wallet.receiving_model.card.milestone_status" tag="p" class="default-model-milestone-status">
          <template #status>
            <strong>
              {{ t('my_wallet.receiving_model.card.milestone_value', { completed: item.completed, total: item.total }) }}
            </strong>
          </template>
        </I18nT>
      </li>
      <li v-else-if="item.kind === 'fallback'" class="default-model-condition">
        <ConditionCheck v-if="isChecklist" />
        <span class="default-model-condition-text">{{ t(`${CONDITIONS_KEY_PATH}.fallback`) }}</span>
      </li>
      <li v-else class="default-model-condition">
        <ConditionCheck v-if="isChecklist" />
        <I18nT :keypath="`${item.keyPath}.text`" tag="span" class="default-model-condition-text">
          <template #highlight>
            <strong>{{ t(`${item.keyPath}.highlight`, { value: item.value }) }}</strong>
          </template>
          <template #days>
            {{ PERFORMANCE_WINDOW_DAYS }}
          </template>
        </I18nT>
      </li>
    </template>

    <li v-if="mode === 'novice'" class="default-model-condition">
      <I18nT keypath="my_wallet.receiving_model.card.novice_next.text" tag="span">
        <template #highlight>
          <strong>{{ t('my_wallet.receiving_model.card.novice_next.highlight') }}</strong>
        </template>
        <template #onTime>
          <span class="default-model-underline">{{ t('my_wallet.receiving_model.card.novice_next.on_time') }}</span>
        </template>
      </I18nT>
    </li>

    <li v-if="mode === 'essential'" class="default-model-condition">
      <ConditionCheck />
      <I18nT keypath="my_wallet.receiving_model.card.essential_condition.text" tag="span" class="default-model-condition-text">
        <template #highlight>
          <strong>{{ t('my_wallet.receiving_model.card.essential_condition.highlight') }}</strong>
        </template>
      </I18nT>
    </li>
  </ul>
</template>

<style scoped lang="scss">
.default-model-conditions {
  @apply flex flex-col gap-4 pl-5 list-disc fonts-body-large-regular text-neutral-low-dark;
  line-height: 1.5;

  &.-checklist {
    @apply pl-0 list-none;

    .default-model-condition {
      @apply flex items-start gap-2;
    }

    .default-model-condition-text {
      @apply flex-1 min-w-0;
    }
  }

  .default-model-milestone {
    @apply flex flex-col items-start gap-1.5 list-none;
  }

  .default-model-error {
    @apply list-none;
  }

  .default-model-milestone-status {
    @apply fonts-body-small-regular text-neutral-low-light;
  }

  .default-model-underline {
    @apply underline;
  }
}
</style>
